// GAVION task actions « Noter »: a note on the task's compagnie (same as the lead page « Ajouter une note »), the task
// marked done unless the rep unticks « Marquer la tâche faite », or « Appel fait en dehors du CRM » (off-CRM call).
import { useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useNotify, useTranslate, useUpdate, type Identifier } from "ra-core";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { OffCrmCallDialog } from "../dialer/offcrm/OffCrmCallDialog";
import type { Lead } from "../leads/types";
import { useAddCompanyNote } from "../leads/show/useAddCompanyNote";
import type { ActionTask } from "./taskActionTypes";

export const TaskNoteDialog = ({
  task,
  companyId,
  company,
  open,
  onOpenChange,
}: {
  task: ActionTask;
  companyId: Identifier;
  company: Lead | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const markDoneId = useId();
  const { save: saveNote, isPending } = useAddCompanyNote(companyId);
  const [update] = useUpdate();
  const [text, setText] = useState("");
  const [markDone, setMarkDone] = useState(true);
  const [offCrm, setOffCrm] = useState(false);

  const markTaskDone = () =>
    update("tasks", {
      id: task.id,
      data: { done_date: new Date().toISOString() },
      previousData: task,
    });

  const save = () =>
    saveNote(text, () => {
      if (markDone) markTaskDone();
      setText("");
      setMarkDone(true);
      onOpenChange(false);
    });

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{translate("gavion.tasks.note.title")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{task.text}</p>
          <Textarea
            aria-label={translate("gavion.lead.note.title")}
            placeholder={translate("gavion.lead.note.placeholder")}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) save();
            }}
          />
          <div className="flex items-center gap-2">
            <Checkbox
              id={markDoneId}
              checked={markDone}
              onCheckedChange={(v) => setMarkDone(v === true)}
            />
            <Label htmlFor={markDoneId}>
              {translate("gavion.tasks.note.mark_done")}
            </Label>
          </div>
          <DialogFooter className="flex-wrap items-center gap-2 sm:justify-between">
            <Button
              type="button"
              variant="link"
              className="h-auto px-0"
              onClick={() => {
                onOpenChange(false);
                setOffCrm(true);
              }}
            >
              {translate("gavion.tasks.note.off_crm")}
            </Button>
            <Button type="button" onClick={save} disabled={isPending}>
              {translate("gavion.lead.note.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {offCrm ? (
        <OffCrmCallDialog
          companyId={Number(companyId)}
          open
          onOpenChange={(next) => !next && setOffCrm(false)}
          onDone={() => {
            setOffCrm(false);
            notify("gavion.dialer.off_crm.done", { type: "success" });
            queryClient.invalidateQueries({ queryKey: ["v_timeline"] });
            // The call worked the task: the same « Marquer la tâche faite » choice applies.
            if (markDone) markTaskDone();
          }}
          leadTimezone={company?.timezone}
          leadEmail={company?.email}
        />
      ) : null}
    </>
  );
};
